import { NavItem } from '@shared/nav-list-item.component'

export interface HomeShortcut {
  displayName: string
  iconName: string
  route: string
  groupName: string
  accentClass: string
  contextLabel: string | null
}

const TOP_LEVEL_ACCENT_CLASSES: Record<string, string> = {
  'Credit SAGE': 'app-palette-accent',
  'Structured Credit': 'app-palette-blue',
  NPI: 'app-palette-green',
  Modeling: 'app-palette-purple',
  'Credit Admin': 'app-palette-red',
}
const DEFAULT_ACCENT_CLASS = 'app-palette-accent'
// GSE gets its own accent (orange) to visually differentiate it from MI, even
// though both sit under the same "Credit SAGE" top-level group.
const GSE_ACCENT_CLASS = 'app-palette-orange'

function getAccentClass(topLevelName: string, immediateParent: string | undefined): string {
  if (immediateParent === 'GSE') {
    return GSE_ACCENT_CLASS
  }
  return TOP_LEVEL_ACCENT_CLASSES[topLevelName] ?? DEFAULT_ACCENT_CLASS
}

function flattenNavTree(
  items: NavItem[],
  ancestry: string[] = [],
  inheritedIcon: string | null = null,
): HomeShortcut[] {
  return items.flatMap((item) => {
    const icon = item.iconName ?? inheritedIcon

    if (item.children && item.children.length) {
      return flattenNavTree(item.children, [...ancestry, item.displayName], icon)
    }

    if (!item.route) {
      return []
    }

    const topLevelName = ancestry.length ? ancestry[0] : item.displayName
    const immediateParent = ancestry.length ? ancestry[ancestry.length - 1] : undefined

    return [
      {
        displayName: item.displayName,
        iconName: icon ?? 'arrow_forward',
        route: item.route,
        groupName: ancestry.length ? ancestry.join(' — ') : item.displayName,
        accentClass: getAccentClass(topLevelName, immediateParent),
        contextLabel: ancestry.length > 1 ? ancestry[ancestry.length - 1] : null,
      },
    ]
  })
}

export function flattenAllNavItems(items: NavItem[]): HomeShortcut[] {
  return flattenNavTree(items)
}

export function flattenEnabledNavItems(
  items: NavItem[],
  allowedGuards: Set<string>,
): HomeShortcut[] {
  return flattenAllNavItems(items).filter((shortcut) => allowedGuards.has(shortcut.route))
}
